import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from 'axios';

import log from '@src/datadog';

import { BLACKLIST_AUTH_PATH, HTTPMethods } from './constants';
import UserManagerClient from './UserManagerClient';
interface ParamsProps<T1 = null> {
  path: string;
  method: 'get' | 'post' | 'put' | 'delete' | 'patch';
  params?: T1;
  data?: T1;
}
type AxiosOptionalConfig = Omit<
  AxiosRequestConfig,
  'url' | 'method' | 'data' | 'params'
>;

const httpService: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASEURL ?? 'http://localhost:3000',
  headers: {
    'Content-Type': 'application/json',
  },
});

const onResponseError = async (error: AxiosError): Promise<AxiosError> => {
  if (error?.response?.status === 401) {
    const user = await UserManagerClient.getUser();

    await UserManagerClient.removeUser();
    await UserManagerClient.revokeTokens(['access_token', 'refresh_token']);
    await UserManagerClient.clearStaleState();

    if (user?.id_token) {
      await UserManagerClient.signoutRedirect({
        post_logout_redirect_uri: window.location.origin,
        id_token_hint: user.id_token,
      });
    } else {
      await UserManagerClient.signinRedirect();
    }

    log.clearDDUser();
  } else {
    log.sendAxiosError(error);
  }

  return Promise.reject(error);
};

httpService.interceptors.request.use(
  (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => request,
  (error: AxiosError): Promise<AxiosError> => Promise.reject(error),
);
httpService.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  (error: AxiosError): Promise<AxiosError> => onResponseError(error),
);
class Service {
  async fetchAPI<Params, Responses>(
    parameters: ParamsProps<Params>,
    options?: AxiosOptionalConfig,
  ): Promise<AxiosResponse<Responses>> {
    const { path: url, method, data, params } = parameters;

    const { headers, responseType } = options ?? {};

    const requestOptions: AxiosRequestConfig = {
      url,
      method,
      data: data ?? null,
      params: params ?? null,
      headers: headers ?? {},
      responseType: responseType ?? 'json',
    };

    let accessToken = import.meta.env.VITE_KEYCLOAK_MOCK_NIK ?? '';

    if (import.meta.env.VITE_KEYCLOAK_ENABLE) {
      const user = await UserManagerClient.getUser();
      accessToken = user?.access_token ?? '';
    }

    if (
      accessToken &&
      !BLACKLIST_AUTH_PATH.find(pathname => url.includes(pathname))
    ) {
      requestOptions.headers =
        import.meta.env.VITE_KEYCLOAK_ENABLE !== 'true'
          ? {
              ...requestOptions.headers,
              'x-nik': accessToken,
            }
          : {
              ...requestOptions.headers,
              Authorization: `Bearer ${accessToken}`,
            };
    }

    return await httpService(requestOptions);
  }

  async get<Params, Responses>(
    path: string,
    params?: Params,
    options?: AxiosOptionalConfig,
  ): Promise<AxiosResponse<Responses>> {
    const method = HTTPMethods.Get;
    return await this.fetchAPI<typeof params, Responses>(
      {
        path,
        method,
        params,
      },
      options,
    );
  }

  async post<Params, Responses>(
    path: string,
    data: Params,
    options?: AxiosOptionalConfig,
  ): Promise<AxiosResponse<Responses>> {
    const method = HTTPMethods.Post;
    return await this.fetchAPI<typeof data, Responses>(
      {
        path,
        method,
        data,
      },
      options,
    );
  }

  async patch<Datas, Responses>(
    path: string,
    data: Datas,
    params?: Datas,
  ): Promise<AxiosResponse<Responses>> {
    const method = HTTPMethods.Patch;
    return await this.fetchAPI<typeof data, Responses>({
      path,
      method,
      data,
      params,
    });
  }

  async put<Datas, Responses>(
    path: string,
    data?: Datas,
    params?: Datas,
  ): Promise<AxiosResponse<Responses>> {
    const method = HTTPMethods.Put;
    return await this.fetchAPI<typeof data, Responses>({
      path,
      method,
      data,
      params,
    });
  }
}

const ServiceAPI = new Service();

export { ServiceAPI as default };
